<!-- Video dialog (#videoModal, data in data.videos): new or existing video of the plan. Tabs: Details | a second tab that
     depends on the Type (SEO, Publishing, Caption, Live stream, Web SEO) | Released and Metrics (published or Ready videos).
     Details, in order: content Type (it decides the image shape, the fields shown, Trial Reel, the status names and the
     second tab: KIND_INFO in src/features/video.ts); 1. the video: image (1280 px, URL field; ImageBox.vue), project,
     title (required), format (FormatPicker.vue), Trial Reel (Short Form only), voice note (when present: play, remove);
     2. script (ScriptPicker.vue; not for Posts), external link, notes with @mentions (video.notes), linked sources and
     saved videos (VideoRefs.vue: video.sourceIds / savedIds); 3. workflow: status
     (in the Type's words), the published box (PublishedBox.vue), priority, the dates (WorkSlotField.vue: work start and end of work =
     publish day, each with its day and time) with the work line, checklist (Settings › Plan); the last box points to the second tab.
     The second tab is SeoPanel.vue (inside #videoSeo), the Released tab PublishedPanel.vue (inside #videoPub): each is
     drawn again from the start when its tab is opened; their drafts (seo, pub, published) are in the video store and
     saved with the video. "Create Shorts from the script" is ShortButton.vue (in ScriptPicker.vue).
     Opened with openVideoModal(id?, prefill?) or the video store's open(); like the legacy dialog, it closes
     only with ‹, × or the buttons (no Esc, no outside click); Enter in the title, Ctrl/Cmd+S and Ctrl/Cmd+Enter save. From
     1024 px the Details tab has two columns: 1 and 2 on the left, 3 on the right. An audio dropped on it becomes the voice note, an image the
     thumbnail. -->
<script setup lang="ts">
import EditDialogFooter from '../fields/EditDialogFooter.vue'
import { INPUT_CLS, requiredCls } from '../../utils/fieldCls'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import VoicePlayer from '../VoicePlayer.vue'
import AppDialog from '../AppDialog.vue'
import ImageBox from '../fields/ImageBox.vue'
import ProjectPicker from '../fields/ProjectPicker.vue'
import MentionField from '../fields/MentionField.vue'
import PillGroup from '../fields/PillGroup.vue'
import WorkSlotField from '../fields/WorkSlotField.vue'
import VideoTabs from '../video/VideoTabs.vue'
import FormatPicker from '../video/FormatPicker.vue'
import ScriptPicker from '../video/ScriptPicker.vue'
import VideoRefs from '../video/VideoRefs.vue'
import SeoPanel from '../video/SeoPanel.vue'
import PublishedPanel from '../video/PublishedPanel.vue'
import MetricsPanel from '../video/MetricsPanel.vue'
import PublishedBox from '../video/PublishedBox.vue'
import { useI18n } from '../../composables/useI18n'
import { useProfileData } from '../../composables/useProfileData'
import { useDataStore } from '../../stores/data'
import { useVideoStore } from '../../stores/video'
import { uiConfirm, uiAlert } from '../../stores/notices'
import { KIND_INFO, formKind, imageRatio, projectChanged, setFormat, sideImage, sideWidth, statusOptions, workInfo, type VideoForm } from '../../features/video'
import { formatKind } from '../../data/contentTypes'
import { MAX_AUDIO, dropFile } from '../../features/drop'
import { mediaPut, mediaUrl } from '../../features/cloud/media'
import { hasFiles, readImg } from '../../utils/image'
import { enterKey } from '../../features/dialogs'
import { finePointer } from '../../utils/media'

const emit = defineEmits<{ close: [] }>()
const { t, tv } = useI18n()
const data = useProfileData()
const store = useDataStore()
const video = useVideoStore()
// The form of this opening (the store's; it stays this object until the dialog closes)
const f = video.form as VideoForm

const kind = computed(() => formKind(f))
const K = computed(() => KIND_INFO[kind.value])
const side = computed(() => sideImage(kind.value))
const types = computed(() => data.value.settings.formats.map(name => ({ name, on: name === f.format, info: KIND_INFO[formatKind(name)] })))
// The wide box has a fixed height; the side one takes its shape from the aspect ratio
const boxClass = computed(() => `bg-thumbBg border border-red-950/40 rounded-xl ${side.value ? '' : 'h-52 '}flex flex-col items-center justify-center cursor-pointer relative overflow-hidden bg-cover bg-center transition hover:border-brand/60 group`)
const boxStyle = computed(() => (imageRatio(kind.value) ? `aspect-ratio: ${imageRatio(kind.value)};` : ''))
const colStyle = computed(() => (side.value ? { width: sideWidth(kind.value), flexShrink: '0' } : undefined))
const statuses = computed(() => statusOptions(kind.value))
const priorities = [{ value: 'Alta', label: t('High') }, { value: 'Media', label: t('Medium') }, { value: 'Bassa', label: t('Low') }]
const reels = [{ value: 'No', label: 'No' }, { value: 'Sì', label: t('Yes') }]
const checklist = computed(() => data.value.settings.checklist)
// Work start / end and delivery line (the saved video gives the actual delivery, doneAt)
const work = computed(() => workInfo(f, f.id ? data.value.videos.find(v => v.id === f.id) || null : null, new Date()))
const WORK_ICON: Record<string, string> = { deliveredLate: 'fas fa-flag-checkered text-brand mr-1', deliveredOnTime: 'fas fa-circle-check text-green-400 mr-1',
  willBeLate: 'fas fa-flag-checkered text-brand mr-1', willBeOnTime: 'fas fa-flag-checkered text-brand mr-1', overdue: 'fas fa-triangle-exclamation text-brand mr-1',
  duration: 'fas fa-bars-progress text-brand mr-1' }

// The title: a new video starts in it (with a mouse: no phone keyboard over the dialog); after "Enter at least the video
// title!" the cursor goes back in it; Enter in it saves
const titleInput = ref<HTMLInputElement | null>(null)
watch(() => video.titleFocus, () => nextTick(() => titleInput.value?.focus()))
watch(() => f.title, v => { if (v.trim()) video.missingTitle = false })
const onEnter = enterKey(() => video.save())
const saveNow = () => video.save()

// The video's format must belong to its project
watch(() => f.projectId, () => projectChanged(store.getData(), f))

// Every opening of the second tab or of Released starts from the top
const seoBox = ref<HTMLElement | null>(null), pubBox = ref<HTMLElement | null>(null)
watch(() => [video.tab, video.tabSeq], () => {
  if (video.tab === 'seo' && seoBox.value) seoBox.value.scrollTop = 0
  if (video.tab === 'pub' && pubBox.value) pubBox.value.scrollTop = 0
}, { flush: 'post' })

// ===== Voice note =====
const audio = ref<InstanceType<typeof VoicePlayer> | null>(null)
async function removeVoice() {
  if (await uiConfirm(t('Remove the voice note from this video? (applies when you save)'), { ok: t('Remove'), danger: true })) f.voiceNote = ''
}

// ===== Drop on the dialog: an audio becomes the voice note, an image the thumbnail =====
function dragOver(e: DragEvent) { if (hasFiles(e)) e.preventDefault() }
async function drop(e: DragEvent) {
  const file = e.dataTransfer ? dropFile(e) : null
  if (!file) return
  e.preventDefault()
  if (file.type.startsWith('audio/')) {
    if (file.size > MAX_AUDIO) { uiAlert(t('Audio file too large (maximum 10 MB).')); return }
    f.voiceNote = await mediaPut(file, 'voice')
  } else if (file.type.startsWith('image/')) f.thumbnail = await mediaPut(await readImg(file, 1280), 'img')
  else uiAlert(t('In the video dialog you can drop an audio (it becomes the voice note) or an image (the thumbnail).'))
}
let overlay: HTMLElement | null = null
onMounted(() => {
  if (!f.id && finePointer()) nextTick(() => titleInput.value?.focus())
  overlay = document.getElementById('videoModal')
  overlay?.addEventListener('dragover', dragOver)
  overlay?.addEventListener('drop', drop)
})
onBeforeUnmount(() => {
  overlay?.removeEventListener('dragover', dragOver)
  overlay?.removeEventListener('drop', drop)
  audio.value?.pause()
})
</script>

<template>
  <AppDialog id="videoModal" name="video" title-id="modalVideoTitle" :title="f.id ? t('Edit Video') : t('New Video')" width="max-w-2xl" editing :save="saveNow"
    body-class="contents" @close="emit('close')">
    <!-- Dialog tabs: Details | a second tab that depends on the Type | Released -->
    <VideoTabs />

    <!-- Second tab, depending on the content type (drawn again at every opening of the tab: key) -->
    <div id="videoSeo" ref="seoBox" role="tabpanel" :class="['p-6 overflow-y-auto space-y-6 flex-1 text-sm', { hidden: video.tab !== 'seo' }]"><SeoPanel v-if="video.tab === 'seo'" :key="video.tabSeq" /></div>
    <!-- Published video info: where it came out (social links), when, notes -->
    <div id="videoPub" ref="pubBox" role="tabpanel" :class="['p-6 overflow-y-auto space-y-6 flex-1 text-sm', { hidden: video.tab !== 'pub' }]"><PublishedPanel v-if="video.tab === 'pub'" :key="video.tabSeq" /></div>

    <!-- How it is doing on each platform: verdict and numbers -->
    <div id="videoMetrics" role="tabpanel" :class="['p-6 overflow-y-auto space-y-5 flex-1 text-sm', { hidden: video.tab !== 'metrics' }]"><MetricsPanel v-if="video.tab === 'metrics'" :key="video.tabSeq" /></div>

    <div id="videoDetails" role="tabpanel" :class="['p-4 sm:p-6 overflow-y-auto space-y-5 flex-1 text-sm', { hidden: video.tab !== 'details' }]">
      <!-- Type comes first: it decides the image, the fields shown, Trial Reel, the status names and the second tab -->
      <div>
        <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('CONTENT TYPE') }}</label>
        <div id="groupFormat" class="grid gap-1.5 sm:gap-2" style="grid-template-columns:repeat(5,minmax(0,1fr))">
          <button v-for="ty in types" :key="ty.name" type="button" :data-value="ty.name"
            :class="['pill-btn', ty.on ? 'bg-brand text-white border-brand' : 'bg-darkinput text-gray-300 border-gray-800 hover:border-brand/50', 'border rounded-xl px-1 sm:px-2 py-2.5 sm:py-3 flex flex-col items-center gap-1 sm:gap-1.5 font-bold text-[11px] sm:text-xs leading-tight text-center transition min-w-0']"
            @click="setFormat(f, ty.name)">
            <i :class="['fas', ty.info.icon, 'text-base sm:text-lg']"></i><span class="max-w-full break-words">{{ ty.name }}</span><span :class="['hidden sm:block text-[10px] font-semibold', ty.on ? 'opacity-80' : 'text-gray-500']">{{ ty.info.name }}</span></button>
        </div>
        <p id="formatHint" class="text-[11px] text-gray-500 mt-1.5">{{ K.hint }}</p>
      </div>

      <!-- From 1024 px: the video and its script on the left, the workflow on the right -->
      <div class="space-y-5 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-5 lg:items-start">
      <div class="space-y-5 min-w-0">
      <!-- 1. The video -->
      <section class="rounded-2xl border border-gray-800 p-4 space-y-5">
        <div class="flex items-center gap-2 font-black text-gray-100"><span class="w-7 h-7 rounded-lg bg-brand/15 text-brand flex items-center justify-center text-xs"><i id="grpVideoIcon" :class="['fas', K.group[0]]"></i></span><span id="grpVideoTitle">{{ K.group[1] }}</span></div>
        <!-- Vertical and post: cover on the left and fields on the right; horizontal: wide thumbnail on top -->
        <div id="videoMain" :class="side ? 'flex gap-3 sm:gap-5 items-start' : 'space-y-5'">
          <div id="thumbCol" :style="colStyle">
            <ImageBox v-model="f.thumbnail" id="thumbnailBox" file-id="thumbnailFileInput" :max="1280" url-field url-id="inputThumbUrl" remove-id="removeThumbBtn"
              :box-class="boxClass"
              :box-style="boxStyle" remove-class="absolute top-3 right-3 bg-black/70 hover:bg-black text-white p-2 rounded-full text-xs">
              <div id="thumbnailPlaceholder" class="flex flex-col items-center text-brand space-y-2 group-hover:scale-105 transition-transform text-center px-3">
                <i class="fas fa-camera text-3xl"></i>
                <span id="thumbnailLabel" class="font-medium text-xs">{{ K.thumb }}</span>
              </div>
            </ImageBox>
          </div>
          <div id="infoCol" class="space-y-5 min-w-0 flex-1">
            <ProjectPicker v-model="f.projectId" kind="video" :item-id="f.id" />
            <div>
              <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('TITLE') }}</label>
              <input id="inputTitle" ref="titleInput" v-model="f.title" type="text" :placeholder="t('E.g. New camera review')" @keydown.enter="onEnter"
                :class="requiredCls(video.missingTitle)">
            </div>
            <div>
              <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">FORMAT</label>
              <!-- The video's format (e.g. "Super Mario 64", "Trivia"), from its project (stored as "series") -->
              <FormatPicker v-model="f.seriesId" :project-id="f.projectId" />
            </div>
            <!-- Vertical videos only -->
            <div id="reelRow" :class="{ hidden: kind !== 'v' }"><label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('TRIAL REEL (INSTAGRAM ONLY)') }}</label><PillGroup id="groupReel" v-model="f.reelProva" :options="reels" class="flex gap-2 flex-wrap" /></div>
          </div>
        </div>

        <!-- Voice note from "Drop your idea here" (shown only when present) -->
        <div id="videoVoiceBox" :class="{ hidden: !f.voiceNote }">
          <template v-if="f.voiceNote">
            <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('VOICE NOTE') }}</label>
            <div class="flex items-center gap-2 bg-darkinput border border-gray-800 rounded-xl p-2">
              <VoicePlayer id="videoVoiceAudio" ref="audio" :src="mediaUrl(f.voiceNote)" />
              <button type="button" :title="t('Remove the voice note from the video')" class="w-9 h-9 rounded-full bg-darkbtn text-gray-400 hover:text-brand flex-shrink-0" @click="removeVoice"><i class="fas fa-trash text-xs"></i></button>
            </div>
            <p class="text-[11px] text-gray-500 mt-1.5">{{ t('To transcribe it, play it and dictate the text into the Notes with the microphone of your phone keyboard.') }}</p>
          </template>
        </div>
      </section>

      <!-- 2. Script and notes (above the workflow: it is the heart of the work) -->
      <section class="rounded-2xl border border-gray-800 p-4 space-y-5">
        <div class="flex items-center gap-2 font-black text-gray-100"><span class="w-7 h-7 rounded-lg bg-brand/15 text-brand flex items-center justify-center text-xs"><i class="fas fa-pen-nib"></i></span><span id="grpScriptTitle">{{ K.script[0] }}</span></div>
        <div id="scriptRow" :class="{ hidden: !K.script[1] }"><label id="scriptLabel" class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ K.script[1] || t('SCRIPT') }}</label><ScriptPicker :form="f" /><p class="text-[11px] text-gray-500 mt-1.5">{{ t('Every video has its own scripts: start from blank, from a template, from a hook or from a .md / .txt file.') }}</p></div>
        <div>
          <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('EXTERNAL SCRIPT LINK (OPTIONAL)') }}</label>
          <input id="inputLink" v-model="f.link" type="url" inputmode="url" autocapitalize="off" autocorrect="off" spellcheck="false" autocomplete="off" placeholder="https://docs.google.com/..." :class="INPUT_CLS">
        </div>
        <div>
          <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('NOTES') }}</label>
          <!-- Private (ideas, voice-note transcript, @mentions): public texts are in the second tab -->
          <MentionField id="inputNotes" v-model="f.notes" :placeholder="t('Ideas, voice-note transcript, things to remember... (@ to mention). Texts to publish go in the second tab.')"
            class="min-h-[4rem] w-full bg-darkinput border border-gray-800 rounded-lg p-3 text-gray-100 focus:outline-none focus:border-brand transition" />
        </div>
        <!-- Linked sources and saved videos (the video's references) -->
        <VideoRefs :form="f" />
      </section>
      </div>

      <!-- 3. Workflow -->
      <section class="rounded-2xl border border-gray-800 p-4 space-y-5">
        <div class="flex items-center gap-2 font-black text-gray-100"><span class="w-7 h-7 rounded-lg bg-brand/15 text-brand flex items-center justify-center text-xs"><i class="fas fa-list-check"></i></span><span>{{ t('Workflow') }}</span></div>
        <div>
          <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('STATUS') }}</label>
          <PillGroup id="groupStatus" v-model="f.status" :options="statuses" class="flex flex-wrap gap-2" />
          <!-- "Mark as published" / "Back to the plan" -->
          <PublishedBox />
        </div>
        <div>
          <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('PRIORITY') }}</label>
          <PillGroup id="groupPriority" v-model="f.priority" :options="priorities" class="flex flex-wrap gap-2" />
        </div>
        <!-- Work start + end of work = publishing date and time (Calendar, Timeline, deadlines) -->
        <div>
          <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('DATES') }}</label>
          <!-- Two rows, work start and end of work, each with its day and time (WorkSlotField.vue) -->
          <WorkSlotField v-model:work-start="f.workStart" v-model:publish-date="f.publishDate" v-model:publish-time="f.publishTime" :start-label="t('Work start')" :end-label="K.end" />
          <p id="workInfo" class="text-[11px] text-gray-400 mt-1.5"><span v-if="work.kind === 'endBeforeStart'" class="text-brand font-bold"><i class="fas fa-triangle-exclamation mr-1"></i>{{ work.text }}</span><template v-else><i v-if="WORK_ICON[work.kind]" :class="WORK_ICON[work.kind]"></i><span v-html="work.text"></span></template></p>
        </div>
        <div>
          <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">CHECKLIST</label>
          <!-- Items are editable in Settings › Plan -->
          <div id="checklistBox" class="bg-darkinput border border-gray-800 rounded-xl divide-y divide-gray-800">
            <label v-for="it in checklist" :key="it.id" class="flex items-center justify-between p-3.5 cursor-pointer">
              <span class="font-medium text-gray-200">{{ tv(it.label) }}</span>
              <input v-model="f.checklist[it.id]" type="checkbox" :data-ck="it.id" class="w-5 h-5 accent-brand rounded cursor-pointer"></label>
          </div>
        </div>
      </section>
      </div>

      <!-- Last step: points to the second tab, which changes with the Type -->
      <div class="rounded-2xl border border-brand/50 bg-gradient-to-br from-brand/15 to-transparent p-4 flex items-center gap-3">
        <span class="w-11 h-11 rounded-xl bg-brand text-white flex items-center justify-center flex-shrink-0"><i id="nextStepIcon" :class="['fas', K.next[0]]"></i></span>
        <div class="flex-1 min-w-0"><div id="nextStepTitle" class="font-black text-gray-100">{{ K.next[1] }}</div>
          <div id="nextStepText" class="text-[11px] text-gray-400">{{ K.next[2] }}</div></div>
        <button type="button" class="px-4 py-2.5 rounded-xl bg-brand hover:bg-brandDark text-white text-xs font-bold flex-shrink-0" @click="video.setTab('seo')"><span>{{ t('Go') }}</span> <i class="fas fa-arrow-right ml-1"></i></button>
      </div>
    </div>

    <template #footer>
      <EditDialogFooter delete-id="deleteVideoBtn" :can-delete="!!f.id" :delete-label="t('Delete video')" :save-label="t('Save video')" @delete="video.remove()" @save="video.save()" />
    </template>
  </AppDialog>
</template>
